/**
 * Pieces shared by the four vault tabs.
 *
 * Not exported from the barrel: these are internal to components/vault/ and the
 * rest of the app has its own equivalents (EmptyState, the song grid's truncation).
 * They exist because the prototype's vault repeated the same muted one-liner and
 * the same ellipsis triple a dozen times.
 */
import type { useVault } from '../../hooks';
import { c } from '../../styles/theme';

/**
 * Everything the vault tabs need, which is the whole hook.
 *
 * Passing the controller wholesale rather than spreading ~25 props per tab: these
 * components are slices of one screen, not reusable widgets, and the alternative is
 * a 25-line prop list per tab that has to change every time the store gains a
 * field. Derived from the hook so the two cannot drift.
 */
export type VaultController = ReturnType<typeof useVault>;

/** Muted one-line note used for every empty list and section caption. */
export function Note({ children, marginBottom }: { children: string; marginBottom?: number }) {
  return (
    <p style={{ fontSize: 12, color: c.text3, margin: `0 0 ${marginBottom ?? 8}px` }}>{children}</p>
  );
}

/** Prompt/preset row on the Recent tab: name on the left, date on the right. */
export function RecentRow({ label, date }: { label: string; date: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 12,
        padding: '6px 0',
        borderBottom: `1px solid ${c.border}`,
      }}
    >
      <span>{label}</span>
      <span style={{ color: c.text3 }}>{date}</span>
    </div>
  );
}

/** Single-line clamp, spread into the style objects that need it. */
export const TRUNCATE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;
